import { forwardRef, type ReactNode } from 'react'

import { AnimatePresence, motion, type MotionStyle } from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'

const wipeStyle = {
  '--skeleton-wipe': '100%',
  maskImage:
    'linear-gradient(to right, black var(--skeleton-wipe), transparent calc(var(--skeleton-wipe) + 100%))'
} satisfies MotionStyle & { '--skeleton-wipe': string }

const wipeVisible = {
  '--skeleton-wipe': '100%',
  transition: { duration: 0 }
}
const wipeExit = {
  '--skeleton-wipe': '-100%',
  transition: { duration: 0.6, ease: 'easeInOut' }
} as const

const fallbackProps = {
  'aria-hidden': true,
  'data-slot': 'skeleton-fallback',
  inert: '',
  className:
    'pointer-events-none col-start-1 row-start-1 z-10 rounded-[inherit] bg-inherit'
} as const

export type SkeletonRevealProps = React.ComponentPropsWithRef<'div'> & {
  loading: boolean
  fallback: ReactNode
  children: ReactNode
}

/**
 * Keeps the placeholder over loaded content until the 600ms wipe finishes.
 * Children mount when loading ends; the fallback owns its placeholder layout.
 * Reduced motion switches immediately, including when enabled during a wipe.
 */
export const SkeletonReveal = forwardRef<HTMLDivElement, SkeletonRevealProps>(
  ({ loading, fallback, children, className, ...props }, ref) => {
    const reducedMotion = useReducedMotion()

    return (
      <div
        ref={ref}
        role='group'
        data-slot='skeleton-reveal'
        aria-busy={loading}
        {...props}
        className={cn('bg-surface relative grid', className)}
      >
        {!loading ? (
          <div data-slot='skeleton-content' className='col-start-1 row-start-1'>
            {children}
          </div>
        ) : null}
        {reducedMotion ? (
          loading ? (
            <div {...fallbackProps}>{fallback}</div>
          ) : null
        ) : (
          <AnimatePresence initial={false}>
            {loading ? (
              <motion.div
                key='skeleton'
                {...fallbackProps}
                style={wipeStyle}
                initial={wipeVisible}
                animate={wipeVisible}
                exit={wipeExit}
              >
                {fallback}
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}
      </div>
    )
  }
)
SkeletonReveal.displayName = 'SkeletonReveal'
